// The browser half of the diagnostics (owner's request, 2026-09-23): how fast
// the page was for this visitor, and any error the page ran into.
//
// It must cost the visitor nothing. Measurements wait in memory and leave in
// one small beacon when the page is hidden or closed - the browser sends a
// beacon after the page has gone, so nobody waits for it. Errors leave a
// couple of seconds after they happen, a few at most per page. No cookies,
// no identifiers: the address of the page (without its query string), the
// kind of screen, the numbers.

export const TELEMETRY_URL = '/api/telemetry';
const MAX_ERRORS_PER_PAGE = 5;
const ERROR_DELAY_MS = 2000;

type VitalEntry = { name: string; value: number };
type ErrorEntry = { message: string; stack: string; kind: 'error' | 'rejection' | 'boundary'; digest?: string; path: string };

let landingPath = '';
let vitals: VitalEntry[] = [];
let errors: ErrorEntry[] = [];
const seenErrors = new Set<string>();
let errorTimer: ReturnType<typeof setTimeout> | null = null;

function device(): 'mobile' | 'desktop' {
  return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
}

function send(body: Record<string, unknown>) {
  const data = JSON.stringify(body);
  try {
    if (navigator.sendBeacon?.(TELEMETRY_URL, new Blob([data], { type: 'application/json' }))) return;
  } catch {
    // Fall through to fetch.
  }
  fetch(TELEMETRY_URL, { method: 'POST', body: data, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => undefined);
}

/**
 * The page a load's measurements belong to. Set once, on the first page of
 * the visit: speed is measured for the page the browser loaded, not for the
 * pages opened inside it afterwards.
 */
export function startTelemetry() {
  if (landingPath) return;
  landingPath = window.location.pathname;
  const flush = () => {
    if (document.visibilityState === 'hidden') flushVitals();
  };
  document.addEventListener('visibilitychange', flush);
  window.addEventListener('pagehide', flushVitals);
  window.addEventListener('error', (event) => {
    // A resource that failed to load, or a script from another site that the
    // browser hides the details of ("Script error.") - nothing to act on.
    if (!(event instanceof ErrorEvent) || !event.message || event.message === 'Script error.') return;
    reportClientError(event.error ?? event.message, 'error');
  });
  window.addEventListener('unhandledrejection', (event) => reportClientError(event.reason, 'rejection'));
}

export function addVital(name: string, value: number) {
  // Administration pages are for a handful of people; the numbers that
  // matter are the public site's.
  if (!landingPath || landingPath.startsWith('/admin')) return;
  vitals = vitals.filter((v) => v.name !== name);
  vitals.push({ name, value });
}

function flushVitals() {
  if (vitals.length === 0) return;
  send({ path: landingPath, device: device(), vitals });
  vitals = [];
}

/** An error the page ran into, whether thrown, rejected or caught by an error page. */
export function reportClientError(error: unknown, kind: ErrorEntry['kind']) {
  if (typeof window === 'undefined') return;
  const err = (error && typeof error === 'object' ? error : { message: String(error) }) as {
    message?: string;
    stack?: string;
    digest?: string;
  };
  const message = String(err.message || '').slice(0, 500);
  const stack = String(err.stack || '').slice(0, 2000);
  if (!message) return;
  // Browser add-ons throw into every page they touch; those errors are theirs.
  if (/(chrome|moz|safari(-web)?)-extension:\/\//.test(stack)) return;
  if (seenErrors.has(message) || seenErrors.size >= MAX_ERRORS_PER_PAGE) return;
  seenErrors.add(message);
  errors.push({ message, stack, kind, digest: err.digest, path: window.location.pathname });
  if (!errorTimer) {
    errorTimer = setTimeout(() => {
      errorTimer = null;
      const batch = errors;
      errors = [];
      if (batch.length > 0) send({ device: device(), errors: batch });
    }, ERROR_DELAY_MS);
  }
}
